import { CrewUi } from 'corso-types';
import api from '~/utils/api';
import {
  extractCreditResolutionMethods,
  isCreditClaim,
  pendingClaimLineItemToCalculatedLineItem,
} from '~/utils/claim-line-item';
import { sumPrice, sumTax } from '~/utils/compute';
import { toCurrency } from '~/utils/formatters';
import {
  makeCrewClaimWithClaimLineItemsLoader,
  redirect,
} from '~/utils/routing';

const defaultGiftCardTextContent =
  'Get a gift card for store credit. A redeemable code will be sent via email once your return has been approved.';

const defaultRefundTextContent =
  'Disclaimer: Receive a refund to your original payment method once your return has been approved. If you choose this method, the final amount may be subject to a return handling fee. The handling fee amount will be calculated for your review before you submit your request.';

export const { loader, useLoaderData: useRefundOptionsData } =
  makeCrewClaimWithClaimLineItemsLoader(async ({ context }) => {
    if (context.crewClaim.claimType === 'Warranty')
      return redirect(`../address`);

    const {
      storefrontId,
      theme: { offerGiftCardDetailText, offerRefundDetailText },
      return: {
        isRefundEnabled: isSettingsRefundEnabled,
        isGiftCardEnabled: isSettingsGiftCardEnabled,
      },
    } = context.settings;

    const formatCurrency = toCurrency(context.order.currencyCode);
    const creditClaimLineItems =
      context.crewClaim.claimLineItems.filter(isCreditClaim);

    if (!creditClaimLineItems.length) {
      return redirect(`../address`);
    }

    const refundSubtotal = sumPrice(creditClaimLineItems);
    const taxRefundValue = sumTax(creditClaimLineItems);

    const {
      fromCustomer: {
        adjustments: { incentive },
      },
      returnSummary,
    } = await api.calculateBeforeClaimSubmission({
      params: { storefrontId },
      body: {
        id: context.order.id,
        claimType: context.crewClaim.claimType,
        calculatedLineItems: context.crewClaim.claimLineItems.map((cli) =>
          pendingClaimLineItemToCalculatedLineItem(cli),
        ),
        creditResolutionMethods:
          extractCreditResolutionMethods(creditClaimLineItems),
      } satisfies CrewUi.CalculatedOrder,
    });

    // * no monetary claims so skip to address
    if (returnSummary) {
      return redirect(`../address`);
    }

    const giftCardTextContent =
      offerGiftCardDetailText ?? defaultGiftCardTextContent;

    const refundTextContent = offerRefundDetailText ?? defaultRefundTextContent;

    return {
      // *  currently the claim doesn't support mixed monetary resolution methods
      // * so need to check that all claim line items support one method

      isRefundEnabled:
        isSettingsRefundEnabled &&
        creditClaimLineItems.every(
          (cli) => cli.lineItem.return.refund.isEligible,
        ),
      isGiftCardEnabled:
        isSettingsGiftCardEnabled &&
        creditClaimLineItems.every(
          (cli) => cli.lineItem.return.giftCard.isEligible,
        ),

      hasIncentive: Boolean(incentive),
      incentive: formatCurrency(incentive),
      refund: formatCurrency(refundSubtotal + taxRefundValue),
      giftCard: formatCurrency(refundSubtotal + taxRefundValue + incentive),
      tax: formatCurrency(taxRefundValue),
      refundTextContent,
      giftCardTextContent,
    };
  });
